import React, { useContext, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import useAuthRoles from '../../../hooks/useAuthRoles';
import Yup from '../../../utils/yupMethods';
import UserService from '../../../services/UserService/UserService';
import UserRole from '../../../types/UserRole';
import User from '../../../types/User';
import UserContext from '../../../context/UserContext/UserContext';
import CustomInput from '../../../components/CustomInput/CustomInput';
import UserRoleSelector from '../UserRoleSelector/UserRoleSelector';
import CustomModal from '../../../components/CustomModal/CustomModal';
import Button from '../../../components/Button/Button';
import useEffectiveOrganisationId from '../../../hooks/useEffectiveOrganisationId';

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  openErrorSnackbar: (message: string) => void;
  openSuccessSnackbar: (message: string) => void;
  onSuccess?: () => void;
}

interface FormValues {
  email: string;
  firstName: string;
  lastName: string;
}

function InviteUserModal({
  open,
  onClose,
  openErrorSnackbar,
  openSuccessSnackbar,
  onSuccess
}: InviteUserModalProps): React.ReactElement {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>('READ_WRITE');
  const { user: loggedInUser } = useContext(UserContext);

  const handleRoleChange = (value: string): void => {
    setSelectedRole(value as UserRole);
  };
  const location = useLocation();
  const { userIsAdmin } = useAuthRoles();
  const effectiveOrganisationId = useEffectiveOrganisationId();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required')
  });

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema)
  });

  if (!userIsAdmin()) {
    return <Navigate to='/chats' state={{ from: location }} replace />;
  }

  const onSubmit = (data: FormValues): void => {
    const { email, firstName, lastName } = data;
    setIsLoading(true);
    UserService.addUser(
      email,
      firstName,
      lastName,
      selectedRole,
      effectiveOrganisationId ?? undefined
    )
      .then(() => {
        onClose();
        openSuccessSnackbar('User added successfully');
        onSuccess?.();
      })
      .catch((err) => {
        if (err.name === 'CanceledError') return;
        const error =
          err.apiError?.message ||
          'Something went wrong. Please try again later.';
        openErrorSnackbar(error);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title='Invite user'
      description='Send an invitation to a new user to join the platform.'
      maxWidth='md'
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Controller
          name='email'
          control={control}
          render={({ field }) => (
            <CustomInput
              label='Email'
              type='email'
              fullWidth
              {...field}
              error={errors.email}
            />
          )}
        />
        <Controller
          name='firstName'
          control={control}
          render={({ field }) => (
            <CustomInput
              label='First name'
              type='text'
              fullWidth
              {...field}
              error={errors.firstName}
            />
          )}
        />
        <Controller
          name='lastName'
          control={control}
          render={({ field }) => (
            <CustomInput
              label='Last name'
              type='text'
              fullWidth
              {...field}
              error={errors.lastName}
            />
          )}
        />
        <UserRoleSelector
          selectedRole={selectedRole}
          handleChange={handleRoleChange}
          loggedInUser={loggedInUser as User}
          fullWidth
        />
        <div className='flex justify-end gap-2 pt-4'>
          <Button
            type='button'
            variant='outline'
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type='submit' loading={isLoading}>
            {isLoading ? 'Sending...' : 'Send invite'}
          </Button>
        </div>
      </form>
    </CustomModal>
  );
}

export default InviteUserModal;
